/**
 * Accordion component
 * @author Carlos Romera <cromera@adasasistemas.com>
 */

// Library
import React, { MouseEventHandler, useState } from 'react';
// Public content
import { Arrow } from '@public/icons';

import { Separator } from '../separator/Separator';

// CSS Style for this module
import styles from './accordion.module.scss';

/**
 * Interface for Accordion props
 */
interface AccordionProps {
	/** Content to be displayed in the header */
	headerContent: React.ReactNode;
	/** Content to be displayed in the body */
	bodyContent: React.ReactNode;
	/** Show the body of the accordion */
	isOpen?: boolean;
	/** Disable the accordion to prevent click*/
	disabled?: boolean;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Accordion generic component
 * @param {AccordionProps} Prop
 * @returns  an accordion that has a header and body and can be toggled to show/hide body
 */
export const Accordion = ({
	headerContent,
	bodyContent,
	isOpen = false,
	disabled,
	testId,
}: AccordionProps) => {
	const [active, setActive] = useState(isOpen);

	const toggleAccordion:
		| MouseEventHandler<HTMLButtonElement | HTMLDivElement>
		| undefined = event => {
		event.stopPropagation();
		setActive(prevState => !prevState);
	};

	return (
		<div className={styles[`accordion-container`]}>
			<div
				className={` ${styles[`accordion-title-container`]} ${
					styles[`accordion-title-container-${active ? 'show' : 'hidden'}`]
				} `}
				onClick={!disabled ? toggleAccordion : undefined}
				data-testid={testId}
			>
				<div className={styles[`accordion-title-container-content`]}>{headerContent}</div>
				<button
					onClick={!disabled ? toggleAccordion : undefined}
					className={` ${styles[`accordion-title-expand`]} ${
						styles[`accordion-title-expand-${active ? 'show' : 'collapse'}`]
					} `}
				>
					<Arrow customStyles={styles['accordion-arrow']} />
				</button>
			</div>
			<Separator customStyles={styles['accordion-separator']} />
			<div
				className={` ${styles[`accordion-body`]} ${styles[`accordion-body-animation`]} ${styles[`accordion-body-${active ? 'show' : 'hidden'}`]} `}
			>
				{bodyContent}
			</div>
		</div>
	);
};
